import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { allocations } from "../../api/endpoints/allocations";
import { allocationVlans } from "../../api/endpoints/allocationVlans";
import { qk } from "../../api/queryKeys";
import { adminQueryOptions } from "../../api/adminQueryOptions";

export interface SiteVlanNames {
  // Generated VLAN names for the site — the template name for each VLAN tag
  // allocated to the site, ordered by tag. This mirrors the backend's
  // site_vlan_names() (allocation ledger ∩ vlan-template), which is the strict
  // validation source for firewall rules + outbound_deny_vlans. There is no
  // read endpoint for this set, so we derive it client-side from the same two.
  names: string[];
  nameByTag: Map<number, string>;
  isPending: boolean;
  error: unknown;
}

const EMPTY = { names: [] as string[], nameByTag: new Map<number, string>() };

export function useSiteVlanNames(
  accountSlug: string,
  siteSlug: string,
): SiteVlanNames {
  const ledgerQ = useQuery({
    ...adminQueryOptions,
    queryKey: qk.allocationLedger(accountSlug),
    queryFn: () => allocations.ledger(accountSlug).then((r) => r.data),
  });
  const templateQ = useQuery({
    ...adminQueryOptions,
    queryKey: qk.vlanTemplate(),
    queryFn: () => allocationVlans.vlanTemplate().then((r) => r.data),
  });

  const derived = useMemo(() => {
    if (!ledgerQ.data || !templateQ.data) return EMPTY;

    const tmplName = new Map<number, string>();
    for (const t of templateQ.data) tmplName.set(t.vlan_tag, t.name);

    // site_slug is globally unique (backend resolves sites by bare slug), so a
    // flat search across the ledger's accounts is safe.
    const site = ledgerQ.data
      .flatMap((a) => a.sites)
      .find((s) => s.site_slug === siteSlug);
    if (!site) return EMPTY;

    const tags = site.vlans
      .map((v) => v.vlan_tag)
      .filter((t): t is number => t != null)
      .sort((a, b) => a - b);

    const nameByTag = new Map<number, string>();
    for (const tag of tags) {
      const name = tmplName.get(tag);
      if (name != null) nameByTag.set(tag, name); // skip tags not in the template
    }
    return { names: [...nameByTag.values()], nameByTag };
  }, [ledgerQ.data, templateQ.data, siteSlug]);

  return {
    names: derived.names,
    nameByTag: derived.nameByTag,
    isPending: ledgerQ.isPending || templateQ.isPending,
    error: ledgerQ.error ?? templateQ.error ?? null,
  };
}
